import { ButtonPrimary } from "components/Atoms/Button";
import React from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { DetailSidePanel } from "../DetailSidePanel";
import { CompanySidePanelDetails } from "./CompanySidePanelDetails";
import { RightClickMenu } from "components/RightClickMenu/RightClickMenuContext";
import { rightMenuForSidePanel } from "components/RightClickMenu/rightClickMenuUtils";
import { useOpenSidePanel } from "../SidePanel";

interface CompanySidePanelProps {
  companyId: number
  trigger: JSX.Element
  onChange?: () => void
  fallbackIfNoPermission?: JSX.Element
}

export const CompanySidePanel = (props: CompanySidePanelProps) => {
  if (!site.permissions.addressBook.canAccess) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <DetailSidePanel
    detailUrl={routes.company_Detail(props.companyId).absolute}
    apiCall={() => window.CoreApi.Company.GetSidePanelData(props.companyId)}
    apiKey="company"
    entityId={props.companyId}
    title={RESX.Company.resxCompany}
    footerContent={
      <ButtonPrimary href={routes.company_Detail(props.companyId).absolute} label={RESX.Buttons.DetailView} />
    }
    trigger={<RightClickMenu menuItems={rightMenuForSidePanel(routes.company_Detail(props.companyId).absolute)}>{props.trigger}</RightClickMenu>}
  >
    {(company, refetch) => <CompanySidePanelDetails
      company={company}
      refetch={refetch}
      viewOnMap
    />}
  </DetailSidePanel>;
};

export const useOpenCompanySidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openCompanySidePanel = React.useCallback((companyId: number) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Company.GetSidePanelData(companyId)}
      apiKey="company"
      entityId={companyId}
      title={RESX.Company.resxCompany}
      footerContent={
        <ButtonPrimary href={routes.company_Detail(companyId).absolute} label={RESX.Buttons.DetailView} />
      }
      skipSidePanelBase
    >
      {(data, refetch) => <CompanySidePanelDetails
        company={data}
        refetch={refetch}
        viewOnMap
      />}
    </DetailSidePanel>, `company-side-${companyId}`);

  }, [openSidePanel]);

  return openCompanySidePanel;
}

export const PromoterSidePanel = (props: CompanySidePanelProps) => {
  if (!site.permissions.addressBook.canAccess) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <DetailSidePanel
    detailUrl={routes.company_Detail(props.companyId).absolute}
    apiCall={() => window.CoreApi.Company.GetSidePanelData(props.companyId)}
    apiKey="promoter"
    entityId={props.companyId}
    title={site.currentEnvironment.functionGroups.promoter.singular}
    footerContent={
      <ButtonPrimary href={routes.company_Detail(props.companyId).absolute} label={RESX.Buttons.DetailView} />
    }
    onOpen={props.onChange}
    trigger={<RightClickMenu menuItems={rightMenuForSidePanel(routes.company_Detail(props.companyId).absolute)}>{props.trigger}</RightClickMenu>}
  >
    {(company, refetch) => <CompanySidePanelDetails
      company={company}
      refetch={refetch}
      viewOnMap={false}
    />}
  </DetailSidePanel>;
};

export const useOpenPromoterSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openPromoterSidePanel = React.useCallback((companyId: number) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Company.GetSidePanelData(companyId)}
      apiKey="promoter"
      entityId={companyId}
      title={site.currentEnvironment.functionGroups.promoter.singular}
      footerContent={
        <ButtonPrimary href={routes.company_Detail(companyId).absolute} label={RESX.Buttons.DetailView} />
      }
      skipSidePanelBase
    >
      {(data, refetch) => <CompanySidePanelDetails
        company={data}
        refetch={refetch}
        viewOnMap={false}
      />}
    </DetailSidePanel>, `promoter-side-${companyId}`);
  }, [openSidePanel]);

  return openPromoterSidePanel;
}

export const ArtistSidePanel = (props: CompanySidePanelProps) => {
  if (!site.permissions.addressBook.canAccess) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <DetailSidePanel
    detailUrl={routes.company_Detail(props.companyId).absolute}
    apiCall={() => window.CoreApi.Company.GetSidePanelData(props.companyId)}
    apiKey="artist"
    entityId={props.companyId}
    title={site.currentEnvironment.functionGroups.artist.singular}
    footerContent={
      <ButtonPrimary href={routes.company_Detail(props.companyId).absolute} label={RESX.Buttons.DetailView} />
    }
    onOpen={props.onChange}
    trigger={<RightClickMenu menuItems={rightMenuForSidePanel(routes.company_Detail(props.companyId).absolute)}>{props.trigger}</RightClickMenu>}
  >
    {(company, refetch) => <CompanySidePanelDetails
      company={company}
      refetch={refetch}
      viewOnMap={false}
    />}
  </DetailSidePanel>;
};

export const useOpenArtistSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openArtistSidePanel = React.useCallback((companyId: number) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Company.GetSidePanelData(companyId)}
      apiKey="artist"
      entityId={companyId}
      title={site.currentEnvironment.functionGroups.artist.singular}
      footerContent={
        <ButtonPrimary href={routes.company_Detail(companyId).absolute} label={RESX.Buttons.DetailView} />
      }
      skipSidePanelBase
    >
      {(data, refetch) => <CompanySidePanelDetails
        company={data}
        refetch={refetch}
        viewOnMap={false}
      />}
    </DetailSidePanel>, `artist-side-${companyId}`);
  }, [openSidePanel]);

  return openArtistSidePanel;
}
